import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { 
  getQuizQuestions, 
  getHouseScores, 
  saveHouseScores, 
  SCHOOL_HOUSE_TEAMS, 
  SCHOOL_REWARD_BADGES,
  getSchoolAwards,
  announceInterHouseChampion,
  getCustomHouses
} from '../utils/quizData';
import { 
  playCorrectChime, 
  playWrongBuzzer, 
  playCountdownBeep, 
  playWinFanfare, 
  playTickSound 
} from '../utils/soundEffects';
import { getSettings } from '../utils/db';
import { useGameExitRoute } from '../utils/navigation';
import ConfettiEffect from '../components/ConfettiEffect';

const { 
  FiClock, FiAward, FiCheck, FiX, FiArrowRight, FiArrowLeft, 
  FiMaximize, FiMinimize, FiRotateCcw, 
  FiUsers, FiSun, FiMoon 
} = FiIcons;

const LiveQuizArena = () => {
  const { exitRoute, exitTitle } = useGameExitRoute();
  const [questions, setQuestions] = useState(getQuizQuestions());
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [houseScores, setHouseScores] = useState(getHouseScores());
  
  // Theme: Day vs Night Mode state
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return document.documentElement.classList.contains('dark') || localStorage.getItem('theme') === 'dark';
  });

  const toggleDayNightMode = () => {
    const nextDark = !isDarkMode;
    setIsDarkMode(nextDark);
    if (nextDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };
  
  // Question Interaction State
  const [selectedOption, setSelectedOption] = useState(null);
  const [isRevealed, setIsRevealed] = useState(false);
  const [timeLeft, setTimeLeft] = useState(15);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [clueStep, setClueStep] = useState(1);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [awardedBadge, setAwardedBadge] = useState(null);
  const [schoolAwards, setSchoolAwards] = useState(getSchoolAwards());
  const [settings, setSettings] = useState(getSettings());

  // Round Completion & Winner Announcement State (prevents repeat log entries)
  const [showRoundFinishedModal, setShowRoundFinishedModal] = useState(false);
  const [isRoundWinnerAnnounced, setIsRoundWinnerAnnounced] = useState(false);
  const [roundAwardType, setRoundAwardType] = useState('🏆 Inter-House Quiz Arena Championship Trophy');
  const [roundPrizeDetails, setRoundPrizeDetails] = useState('Trophy, Gold Medals & Certificates awarded to winning Quiz House team');
  const [roundAnnouncedBy, setRoundAnnouncedBy] = useState('Chief Quiz Master / Event Coordinator');
  const [roundAnnouncedRecord, setRoundAnnouncedRecord] = useState(null);

  // Listen for school awards changes from admin dashboard
  useEffect(() => {
    const handleAwardsUpdate = () => setSchoolAwards(getSchoolAwards());
    window.addEventListener('schoolAwardsUpdated', handleAwardsUpdate);
    window.addEventListener('storage', handleAwardsUpdate);
    return () => {
      window.removeEventListener('schoolAwardsUpdated', handleAwardsUpdate);
      window.removeEventListener('storage', handleAwardsUpdate);
    };
  }, []);

  // Filter questions based on category
  const filteredQuestions = selectedCategory === 'All' 
    ? questions 
    : questions.filter(q => q.category === selectedCategory);

  const currentQ = filteredQuestions[currentIndex] || filteredQuestions[0] || questions[0];

  // Timer countdown hook
  useEffect(() => {
    let timer = null;
    if (isTimerActive && timeLeft > 0 && !isRevealed) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setIsTimerActive(false);
            if (settings.soundEnabled !== false) {
              playWrongBuzzer();
            }
            return 0;
          }
          if (prev <= 5 && settings.soundEnabled !== false) {
            playCountdownBeep(prev === 2);
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isTimerActive, timeLeft, isRevealed, settings.soundEnabled]);

  // Real-time synchronization when questions are uploaded via Excel or CSV
  useEffect(() => {
    const handleQuestionsUpdate = () => {
      setQuestions(getQuizQuestions());
    };
    window.addEventListener('quizQuestionsUpdated', handleQuestionsUpdate);
    window.addEventListener('storage', handleQuestionsUpdate);
    return () => {
      window.removeEventListener('quizQuestionsUpdated', handleQuestionsUpdate);
      window.removeEventListener('storage', handleQuestionsUpdate);
    };
  }, []);

  // Reset interaction when question changes
  useEffect(() => {
    setSelectedOption(null);
    setIsRevealed(false);
    setTimeLeft(15);
    setIsTimerActive(false);
    setClueStep(1);
  }, [currentIndex, selectedCategory]);

  const handleStartTimer = () => {
    setIsTimerActive(true);
  };

  const handleSelectOption = (idx) => {
    if (isRevealed) return;
    setSelectedOption(idx);
    setIsRevealed(true);
    setIsTimerActive(false);

    const isCorrect = idx === currentQ.correctIndex;
    if (settings.soundEnabled !== false) {
      if (isCorrect) {
        playCorrectChime();
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 2500);
      } else {
        playWrongBuzzer();
      }
    }
  };

  const handleRevealMystery = () => {
    setIsRevealed(true);
    if (settings.soundEnabled !== false) {
      playWinFanfare();
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    }
  };

  const nextQuestion = () => {
    if (currentIndex < filteredQuestions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      // Completed all questions in the round! Do not repeat from beginning - show winner announcement screen
      setShowRoundFinishedModal(true);
      if (settings.soundEnabled !== false) playWinFanfare();
    }
  };

  const handleAnnounceRoundWinner = () => {
    if (isRoundWinnerAnnounced) return; // Prevent repeated / duplicate entries in log!

    const customHouses = getCustomHouses();
    const sortedHouses = [...customHouses].sort((a, b) => (houseScores[b.id] || 0) - (houseScores[a.id] || 0));
    const winnerHouse = sortedHouses[0] || customHouses[0];
    const winningPoints = houseScores[winnerHouse.id] || 0;

    const record = announceInterHouseChampion({
      houseId: winnerHouse.id,
      houseName: winnerHouse.name,
      badge: winnerHouse.badge,
      points: winningPoints,
      awardType: roundAwardType,
      eventSource: 'Live Quiz Arena',
      prizeDistributionInfo: roundPrizeDetails,
      announcedBy: roundAnnouncedBy
    });

    setIsRoundWinnerAnnounced(true);
    setRoundAnnouncedRecord(record);
    if (settings.soundEnabled !== false) playWinFanfare();
  };

  const handleRestartQuiz = () => {
    setCurrentIndex(0);
    setShowRoundFinishedModal(false);
    setIsRoundWinnerAnnounced(false);
    setRoundAnnouncedRecord(null);
  };

  const prevQuestion = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else {
      setCurrentIndex(filteredQuestions.length - 1);
    }
  };

  const updateHouseScore = (houseId, delta) => {
    const newScores = {
      ...houseScores,
      [houseId]: Math.max(0, (houseScores[houseId] || 0) + delta)
    };
    setHouseScores(newScores);
    saveHouseScores(newScores);
    if (settings.soundEnabled !== false) {
      if (delta > 0) playTickSound(580, 0.08);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
      setIsFullscreen(false);
    }
  };

  return (
    <div className={`min-h-screen flex flex-col justify-between p-4 md:p-8 select-none overflow-x-hidden relative transition-colors duration-300 ${
      isDarkMode 
        ? 'bg-slate-950 text-white' 
        : 'bg-gradient-to-br from-amber-50/90 via-sky-50/70 to-indigo-50/80 text-slate-900'
    }`}>
      
      {/* Background Soft Glow Accents */}
      <div className={`absolute top-0 left-1/4 w-96 h-96 rounded-full blur-[100px] pointer-events-none ${
        isDarkMode ? 'bg-indigo-600/10' : 'bg-sky-200/40'
      }`}></div>
      <div className={`absolute bottom-0 right-1/4 w-96 h-96 rounded-full blur-[100px] pointer-events-none ${
        isDarkMode ? 'bg-amber-500/10' : 'bg-amber-200/40'
      }`}></div>

      {showConfetti && <ConfettiEffect />}

      {/* TOP HEADER: Branding, Projector Controls & Category Switcher */}
      <header className={`relative z-20 flex flex-wrap items-center justify-between gap-4 pb-4 border-b transition-colors ${
        isDarkMode ? 'border-slate-800' : 'border-indigo-100/80'
      }`}>
        <div className="flex items-center gap-3">
          <Link 
            to={exitRoute} 
            title={exitTitle}
            className={`w-10 h-10 rounded-xl border flex items-center justify-center transition-colors shadow-sm ${
              isDarkMode 
                ? 'bg-slate-900 border-slate-700 text-slate-300 hover:text-white' 
                : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
            }`}
          >
            <SafeIcon icon={FiArrowLeft} />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping"></span>
              <h1 className="text-xl md:text-2xl font-black tracking-wider uppercase bg-clip-text text-transparent bg-gradient-to-r from-amber-500 via-orange-500 to-indigo-600">
                School Live Quiz & Guessing Arena
              </h1>
            </div>
            <span className={`text-[11px] font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Classroom & Auditorium Projector Display Mode
            </span>
          </div>
        </div>

        {/* Category Filter Pills */}
        <div className={`flex items-center gap-1.5 p-1.5 rounded-2xl border transition-colors ${
          isDarkMode ? 'bg-slate-900/80 border-slate-800' : 'bg-white/90 border-slate-200 shadow-sm'
        }`}>
          {['All', 'Science & Space', 'History & Heritage', 'General Knowledge'].map(cat => (
            <button
              key={cat}
              onClick={() => {
                setSelectedCategory(cat);
                setCurrentIndex(0);
              }}
              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                selectedCategory === cat 
                  ? 'bg-gradient-to-r from-amber-400 to-yellow-400 text-slate-950 shadow-md font-black' 
                  : (isDarkMode ? 'text-slate-400 hover:text-white hover:bg-slate-800' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100')
              }`}
            >
              {cat === 'All' ? '🌟 All Topics' : cat}
            </button>
          ))}
        </div>

        {/* Controls: Day/Night Mode Switch & Fullscreen */}
        <div className="flex items-center gap-2">
          {/* Day / Night Mode Switcher Button */}
          <button
            onClick={toggleDayNightMode}
            className={`px-3 py-2 rounded-xl border text-xs font-bold flex items-center gap-1.5 transition-all shadow-sm ${
              isDarkMode 
                ? 'bg-slate-900 border-slate-800 text-yellow-400 hover:bg-slate-800' 
                : 'bg-white border-amber-200 text-amber-700 hover:bg-amber-50'
            }`}
            title="Switch Day / Night Mode"
          >
            <SafeIcon icon={isDarkMode ? FiSun : FiMoon} className="text-base" />
            <span className="hidden sm:inline">{isDarkMode ? 'Day Mode ☀️' : 'Night Mode 🌙'}</span>
          </button>

          <button
            onClick={toggleFullscreen}
            className={`p-2.5 rounded-xl border transition-colors shadow-sm ${
              isDarkMode 
                ? 'bg-slate-900 border-slate-800 hover:bg-slate-800 text-slate-300' 
                : 'bg-white border-slate-200 hover:bg-slate-100 text-slate-700'
            }`}
            title="Toggle Fullscreen"
          >
            <SafeIcon icon={isFullscreen ? FiMinimize : FiMaximize} />
          </button>
        </div>
      </header>

      {/* MAIN PROJECTOR ARENA */}
      <main className="relative z-10 flex-1 my-6 flex flex-col justify-center max-w-6xl mx-auto w-full">
        
        {/* Question Metadata Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full border text-xs font-bold uppercase tracking-wider ${
              isDarkMode 
                ? 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30' 
                : 'bg-indigo-100 text-indigo-800 border-indigo-200 shadow-sm'
            }`}>
              {currentQ.category}
            </span>
            <span className={`px-3 py-1 rounded-full text-xs font-mono font-bold ${
              isDarkMode ? 'bg-slate-800 text-slate-300' : 'bg-white text-slate-700 border border-slate-200 shadow-sm'
            }`}>
              Question {currentIndex + 1} of {filteredQuestions.length}
            </span>
          </div>

          {/* 15-Second Timer Badge */}
          {currentQ.type === 'trivia' && (
            <div className="flex items-center gap-3">
              {!isTimerActive && !isRevealed && (
                <button
                  onClick={handleStartTimer}
                  className="px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white text-xs font-black uppercase tracking-wider shadow-md transition-all flex items-center gap-1.5"
                >
                  <SafeIcon icon={FiClock} /> Start Timer (15s)
                </button>
              )}
              <div className={`flex items-center gap-2 px-4 py-1.5 rounded-xl font-mono font-black text-lg border shadow-sm ${
                timeLeft <= 5 
                  ? 'bg-rose-500/20 text-rose-500 border-rose-500/40 animate-pulse' 
                  : (isDarkMode ? 'bg-slate-900 text-amber-400 border-slate-800' : 'bg-white text-amber-600 border-amber-200')
              }`}>
                <SafeIcon icon={FiClock} />
                <span>{String(timeLeft).padStart(2, '0')}s</span>
              </div>
            </div>
          )}
        </div>

        {/* =========================================
            QUESTION VIEW: TRIVIA MULTIPLE CHOICE
        ========================================= */}
        {currentQ.type === 'trivia' && (
          <div className="space-y-6">
            {/* Big Screen Question Card in Light / Night Mode */}
            <div className={`p-8 md:p-12 rounded-3xl border text-center relative overflow-hidden transition-colors ${
              isDarkMode 
                ? 'bg-slate-900/90 border-slate-800 shadow-[0_20px_50px_rgba(0,0,0,0.5)]' 
                : 'bg-white border-2 border-indigo-100 shadow-[0_15px_40px_rgba(99,102,241,0.08)]'
            }`}>
              <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-amber-400 via-orange-400 to-indigo-500"></div>
              
              <h2 className={`text-2xl md:text-4xl font-extrabold leading-relaxed max-w-4xl mx-auto ${
                isDarkMode ? 'text-white' : 'text-slate-900'
              }`}>
                "{currentQ.question}"
              </h2>
            </div>

            {/* 4 Large Colored Option Tiles */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {currentQ.options.map((opt, idx) => {
                const optLetter = ['A', 'B', 'C', 'D'][idx];
                const isSelected = selectedOption === idx;
                const isCorrect = idx === currentQ.correctIndex;
                
                let tileStyle = isDarkMode 
                  ? "bg-slate-900/80 border-slate-800 text-slate-200 hover:border-slate-600" 
                  : "bg-white border-2 border-slate-200 hover:border-indigo-300 text-slate-800 shadow-sm hover:shadow-md";
                let badgeStyle = isDarkMode ? "bg-slate-800 text-slate-400" : "bg-slate-100 text-slate-600";

                if (isRevealed) {
                  if (isCorrect) {
                    tileStyle = isDarkMode 
                      ? "bg-emerald-500/20 border-emerald-400 text-white shadow-[0_0_30px_rgba(16,185,129,0.3)] animate-pulse" 
                      : "bg-emerald-100/90 border-2 border-emerald-500 text-emerald-950 font-black shadow-lg shadow-emerald-500/10";
                    badgeStyle = "bg-emerald-500 text-white font-black";
                  } else if (isSelected && !isCorrect) {
                    tileStyle = isDarkMode 
                      ? "bg-red-500/20 border-red-500/50 text-red-300" 
                      : "bg-rose-100/90 border-2 border-rose-500 text-rose-950 font-bold";
                    badgeStyle = "bg-red-500 text-white font-black";
                  } else {
                    tileStyle = isDarkMode 
                      ? "opacity-40 bg-slate-900 border-slate-800" 
                      : "opacity-40 bg-slate-100 border-slate-200 text-slate-400";
                  }
                }

                return (
                  <motion.button
                    key={idx}
                    type="button"
                    onClick={() => handleSelectOption(idx)}
                    disabled={isRevealed}
                    whileHover={!isRevealed ? { scale: 1.02 } : {}}
                    whileTap={!isRevealed ? { scale: 0.98 } : {}}
                    className={`p-6 rounded-2xl border-2 text-left flex items-center gap-4 transition-all text-lg md:text-xl font-bold shadow-md cursor-pointer ${tileStyle}`}
                  >
                    <span className={`w-11 h-11 rounded-xl flex items-center justify-center font-black text-lg ${badgeStyle}`}>
                      {optLetter}
                    </span>
                    <span className="flex-1">{opt}</span>
                    {isRevealed && isCorrect && <SafeIcon icon={FiCheck} className="text-2xl text-emerald-500" />}
                    {isRevealed && isSelected && !isCorrect && <SafeIcon icon={FiX} className="text-2xl text-red-500" />}
                  </motion.button>
                );
              })}
            </div>

            {/* Educational Fact Card */}
            <AnimatePresence>
              {isRevealed && currentQ.fact && (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-5 rounded-2xl border flex items-start gap-3 shadow-md ${
                    isDarkMode 
                      ? 'bg-amber-500/10 border-amber-500/30' 
                      : 'bg-amber-50 border-amber-200 text-amber-950 shadow-sm'
                  }`}
                >
                  <span className="text-2xl">💡</span>
                  <div>
                    <h5 className={`font-extrabold text-xs uppercase tracking-wider mb-0.5 ${
                      isDarkMode ? 'text-amber-300' : 'text-amber-800'
                    }`}>
                      Did You Know?
                    </h5>
                    <p className={`text-sm md:text-base font-medium ${
                      isDarkMode ? 'text-slate-200' : 'text-slate-800'
                    }`}>
                      {currentQ.fact}
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* =========================================
            QUESTION VIEW: PEHCHANO KAUN (MYSTERY GUESS)
        ========================================= */}
        {currentQ.type === 'mystery' && (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            
            {/* Left: Mystery Image with Progressive Unblur */}
            <div className="md:col-span-5 flex flex-col items-center">
              <div className={`relative w-64 h-64 sm:w-80 sm:h-80 rounded-3xl overflow-hidden border-4 border-amber-400 shadow-2xl ${
                isDarkMode ? 'bg-slate-900' : 'bg-white'
              }`}>
                <img 
                  src={currentQ.image} 
                  alt="Mystery" 
                  className={`w-full h-full object-cover transition-all duration-700 ${
                    isRevealed 
                      ? 'blur-none filter-none' 
                      : (clueStep === 1 ? 'blur-2xl contrast-200' : clueStep === 2 ? 'blur-lg' : 'blur-sm')
                  }`}
                />
                
                {!isRevealed && (
                  <div className="absolute inset-0 bg-slate-950/40 flex items-center justify-center">
                    <span className="text-5xl animate-bounce">❓</span>
                  </div>
                )}

                {isRevealed && (
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-slate-950 p-4 text-center">
                    <span className="text-xs uppercase font-extrabold text-amber-400 tracking-wider">Revealed Answer</span>
                    <h4 className="text-xl font-black text-white">{currentQ.answer}</h4>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Progressive Clues & Reveal Control */}
            <div className="md:col-span-7 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className={`text-2xl md:text-3xl font-black ${
                  isDarkMode ? 'text-amber-400' : 'text-amber-600'
                }`}>
                  {currentQ.title}
                </h3>
                <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                  isDarkMode ? 'bg-slate-800 text-slate-300' : 'bg-white border border-slate-200 text-slate-700 shadow-sm'
                }`}>
                  {clueStep} of {currentQ.clues.length} Clues Active
                </span>
              </div>

              {/* Clues List */}
              <div className="space-y-3">
                {currentQ.clues.map((clue, idx) => {
                  const isVisible = idx < clueStep;
                  return (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: isVisible ? 1 : 0.35, x: 0 }}
                      className={`p-4 rounded-2xl border text-sm md:text-base font-semibold transition-all ${
                        isVisible 
                          ? (isDarkMode ? 'bg-slate-900 border-amber-500/40 text-slate-100 shadow-md' : 'bg-white border-amber-300 text-slate-900 shadow-sm')
                          : (isDarkMode ? 'bg-slate-900/30 border-slate-800 text-slate-600' : 'bg-slate-100/60 border-slate-200 text-slate-400')
                      }`}
                    >
                      {clue}
                    </motion.div>
                  );
                })}
              </div>

              {/* Clue Advance & Reveal Controls */}
              <div className="flex flex-wrap gap-3 pt-2">
                {clueStep < currentQ.clues.length && !isRevealed && (
                  <button
                    type="button"
                    onClick={() => {
                      setClueStep(clueStep + 1);
                      if (settings.soundEnabled !== false) playTickSound(650, 0.08);
                    }}
                    className="px-5 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs uppercase tracking-wider transition-colors shadow-md"
                  >
                    Unlock Next Clue ({clueStep + 1}/{currentQ.clues.length})
                  </button>
                )}

                {!isRevealed ? (
                  <button
                    type="button"
                    onClick={handleRevealMystery}
                    className="px-6 py-3 rounded-xl bg-gradient-to-r from-amber-400 to-yellow-400 hover:from-amber-500 hover:to-yellow-500 text-slate-950 font-black text-xs uppercase tracking-wider shadow-lg transition-transform active:scale-95"
                  >
                    Reveal Identity 🌟
                  </button>
                ) : (
                  <div className={`p-4 rounded-2xl border text-sm font-semibold w-full shadow-sm ${
                    isDarkMode ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-emerald-50 border-emerald-300 text-emerald-950'
                  }`}>
                    {currentQ.fact}
                  </div>
                )}
              </div>
            </div>

          </div>
        )}

      </main>

      {/* FOOTER: School House Leaderboard & Question Navigation */}
      <footer className={`relative z-20 pt-4 space-y-4 border-t transition-colors ${
        isDarkMode ? 'border-slate-800' : 'border-indigo-100/80'
      }`}>
        
        {/* School House Teams Points Barometer */}
        <div className={`p-3 rounded-2xl border transition-colors ${
          isDarkMode ? 'bg-slate-900/80 border-slate-800' : 'bg-white/90 border-slate-200 shadow-sm'
        }`}>
          <div className="flex items-center justify-between mb-2 px-1">
            <span className={`text-[10px] uppercase font-bold tracking-wider flex items-center gap-1.5 ${
              isDarkMode ? 'text-slate-400' : 'text-slate-600'
            }`}>
              <SafeIcon icon={FiUsers} /> Live House Points Barometer
            </span>
            <span className={`text-[10px] font-mono ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              Click +10 to award points
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {SCHOOL_HOUSE_TEAMS.map(team => {
              const score = houseScores[team.id] || 0;
              return (
                <div key={team.id} className={`p-2.5 rounded-xl border flex items-center justify-between transition-colors ${
                  isDarkMode ? 'bg-slate-950 border-slate-800' : 'bg-slate-50 border-slate-200 shadow-xs'
                }`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{team.badge}</span>
                    <div>
                      <span className={`text-xs font-bold block leading-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {team.name}
                      </span>
                      <span className="text-[10px] font-mono text-amber-500 font-black">{score} pts</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => updateHouseScore(team.id, 10)}
                      className="px-2 py-1 bg-emerald-500/20 hover:bg-emerald-500 text-emerald-600 dark:text-emerald-300 hover:text-white font-black text-[10px] rounded-lg border border-emerald-500/30 transition-colors"
                    >
                      +10
                    </button>
                    <button
                      type="button"
                      onClick={() => updateHouseScore(team.id, -5)}
                      className="px-1.5 py-1 bg-red-500/10 hover:bg-red-500 text-red-600 dark:text-red-400 hover:text-white font-black text-[10px] rounded-lg border border-red-500/30 transition-colors"
                    >
                      -5
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Question Paging Bar */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={prevQuestion}
            className={`px-5 py-2.5 rounded-xl border text-xs font-bold flex items-center gap-2 transition-colors shadow-sm ${
              isDarkMode 
                ? 'bg-slate-900 border-slate-700 hover:bg-slate-800 text-slate-300' 
                : 'bg-white border-slate-300 hover:bg-slate-100 text-slate-700'
            }`}
          >
            <SafeIcon icon={FiArrowLeft} /> Previous Question
          </button>

          {/* Award Badge Trigger */}
          <button
            type="button"
            onClick={() => setAwardedBadge(schoolAwards[0] || { title: 'Special Award' })}
            className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 text-xs font-black uppercase tracking-wider shadow-md hover:scale-105 transition-all flex items-center gap-2 cursor-pointer"
          >
            <SafeIcon icon={FiAward} /> Award Cap / Badge
          </button>

          <button
            type="button"
            onClick={nextQuestion}
            className={`px-5 py-2.5 rounded-xl text-xs font-black flex items-center gap-2 transition-all shadow-lg cursor-pointer ${
              currentIndex === filteredQuestions.length - 1
                ? 'bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 text-slate-950 shadow-amber-500/30 animate-pulse'
                : 'bg-indigo-600 hover:bg-indigo-500 text-white'
            }`}
          >
            {currentIndex === filteredQuestions.length - 1 ? (
              <>Finish & Announce Winner 🏆</>
            ) : (
              <>Next Question <SafeIcon icon={FiArrowRight} /></>
            )}
          </button>
        </div>

      </footer>

      {/* QUIZ ARENA COMPLETED & WINNER ANNOUNCEMENT MODAL */}
      <AnimatePresence>
        {showRoundFinishedModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-md overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className={`border-2 rounded-3xl p-6 sm:p-8 max-w-xl w-full shadow-2xl relative text-left my-8 overflow-hidden ${
                isDarkMode ? 'bg-slate-900 border-amber-500/50 text-white' : 'bg-white border-amber-400 text-slate-900'
              }`}
            >
              {/* Header Gold Bar */}
              <div className="absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500"></div>

              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2.5 py-0.5 rounded-full bg-amber-500/20 text-amber-500 border border-amber-500/30 text-[10px] font-black uppercase">
                      Round Finished
                    </span>
                    <span className="text-xs text-slate-400 font-bold">&bull; All {filteredQuestions.length} Questions Answered</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-black flex items-center gap-2">
                    <span>🎉 Quiz Arena Completed!</span>
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Review final house points, announce the round winner, and log prize distribution details.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setShowRoundFinishedModal(false)}
                  className="p-1.5 rounded-xl text-slate-400 hover:text-slate-200"
                >
                  <SafeIcon icon={FiX} className="text-xl" />
                </button>
              </div>

              {/* House Points Leaderboard */}
              <div className="mb-5">
                <div className="text-[11px] font-black uppercase tracking-wider text-slate-400 mb-2">
                  Final House Points Standings:
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {(() => {
                    const customHouses = getCustomHouses();
                    const sorted = [...customHouses].sort((a, b) => (houseScores[b.id] || 0) - (houseScores[a.id] || 0));
                    return sorted.map((house, idx) => (
                      <div
                        key={house.id}
                        className={`p-2.5 rounded-xl border text-center relative ${
                          idx === 0
                            ? 'bg-amber-500/15 border-amber-500/40 text-amber-400 ring-1 ring-amber-400'
                            : isDarkMode ? 'bg-slate-800/60 border-slate-700' : 'bg-slate-50 border-slate-200'
                        }`}
                      >
                        {idx === 0 && (
                          <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.2 rounded-full bg-amber-400 text-slate-950 text-[9px] font-black uppercase">
                            👑 Winner
                          </span>
                        )}
                        <div className="text-2xl mt-1">{house.badge || '🏆'}</div>
                        <div className="text-xs font-black truncate">{house.name}</div>
                        <div className="text-xs font-mono font-bold text-slate-400 mt-0.5">
                          {houseScores[house.id] || 0} pts
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>

              {/* Winner Announcement Section (Prevents repeated / duplicate logs) */}
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/80 mb-5 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-black uppercase text-amber-500 flex items-center gap-1.5">
                    <SafeIcon icon={FiAward} /> Winner Announcement to Sports Day Log
                  </span>
                  {isRoundWinnerAnnounced && (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-[10px] font-black uppercase">
                      ✓ Logged Once
                    </span>
                  )}
                </div>

                {!isRoundWinnerAnnounced ? (
                  <>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-400 mb-1">
                        Award Trophy / Plaque Title:
                      </label>
                      <input
                        type="text"
                        value={roundAwardType}
                        onChange={e => setRoundAwardType(e.target.value)}
                        className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-xl px-3 py-2 text-xs font-bold outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-400 mb-1">
                        Prize Distribution Information:
                      </label>
                      <textarea
                        rows={2}
                        value={roundPrizeDetails}
                        onChange={e => setRoundPrizeDetails(e.target.value)}
                        placeholder="Details for distributing prizes on stage..."
                        className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-xl px-3 py-1.5 text-xs font-medium outline-none"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={handleAnnounceRoundWinner}
                      className="w-full py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-black text-xs shadow-md hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <SafeIcon icon={FiAward} />
                      <span>Announce Winner to Sports Day Log & Champions 🏆</span>
                    </button>
                  </>
                ) : (
                  <div className="p-3 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs space-y-1">
                    <div className="font-black flex items-center gap-1.5 text-emerald-400">
                      <span>✓ Official Winner Announcement Logged</span>
                    </div>
                    <div>
                      <strong>{roundAnnouncedRecord?.houseName}</strong> crowned as <strong>{roundAnnouncedRecord?.awardType}</strong> ({roundAnnouncedRecord?.points} pts).
                    </div>
                    <div className="text-[11px] text-slate-400">
                      Recorded in Sports Day Log & Match History for prize coordination. Announcement will not repeat.
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-3 border-t border-slate-200 dark:border-slate-700/60">
                <button
                  type="button"
                  onClick={() => setShowRoundFinishedModal(false)}
                  className="px-4 py-2 rounded-xl text-slate-400 hover:text-slate-200 text-xs font-bold"
                >
                  Close Summary
                </button>

                <button
                  type="button"
                  onClick={handleRestartQuiz}
                  className="px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs flex items-center gap-1.5 shadow-md cursor-pointer"
                >
                  <SafeIcon icon={FiRotateCcw} />
                  <span>Start New Quiz Round</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Award Badge Modal */}
      <AnimatePresence>
        {awardedBadge && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md"
          >
            <motion.div 
              initial={{ scale: 0.9, y: 20 }} 
              animate={{ scale: 1, y: 0 }} 
              exit={{ scale: 0.9, y: 20 }}
              className={`border rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl relative text-center ${
                isDarkMode ? 'bg-slate-900 border-amber-500/40 text-white' : 'bg-white border-amber-300 text-slate-900'
              }`}
            >
              <button
                type="button"
                onClick={() => setAwardedBadge(null)}
                className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                  isDarkMode ? 'bg-slate-800 hover:bg-slate-700 text-slate-400' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                }`}
              >
                <SafeIcon icon={FiX} />
              </button>

              <div className="w-16 h-16 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-500 text-3xl flex items-center justify-center mx-auto mb-4">
                🎖️
              </div>
              <span className="text-xs uppercase font-extrabold text-amber-500 tracking-wider">Official Event Award</span>
              <h3 className={`text-2xl font-black mt-1 mb-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                Award Caps & Badges
              </h3>
              <p className={`text-xs mb-6 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                Select which physical reward to present to the winning student or house:
              </p>

              <div className="space-y-2 mb-6">
                {(schoolAwards || []).map(badge => (
                  <button
                    key={badge.id}
                    type="button"
                    onClick={() => {
                      if (settings.soundEnabled !== false) playWinFanfare();
                      alert(`Awarded: ${badge.title}! Voucher coupon: PASS-${Math.floor(100000 + Math.random() * 900000)}`);
                      setAwardedBadge(null);
                    }}
                    className={`w-full p-3 rounded-xl border text-left flex items-center justify-between transition-colors group cursor-pointer ${
                      isDarkMode ? 'bg-slate-950 hover:bg-slate-800 border-slate-800' : 'bg-slate-50 hover:bg-amber-50/60 border-slate-200'
                    }`}
                  >
                    <div>
                      <span className={`text-xs font-bold block group-hover:text-amber-500 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                        {badge.title}
                      </span>
                      <span className={`text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{badge.item}</span>
                    </div>
                    <span className="text-xs font-bold text-amber-500">Award &bull;</span>
                  </button>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default LiveQuizArena;
